import type { PublicPlan as PublicPlanV2 } from '@wix/ambassador-pricing-plans-v2-plan/types';
import type { Plan as PlanV3 } from '@wix/ambassador-pricing-plans-v3-plan/types';
import type { TranslatedPricingDetails } from '@wix/pricing-plans-tpa-api';
import { getPlanPrice, getVariantPrice, isPlanV3 } from '@wix/pricing-plans-utils';
import type { CreateCurrencyFormatter, TFunction } from '@wix/yoshi-flow-editor';
import { getFormattedPriceData } from '../hooks';
import { getFormattedAdditionalFees } from '../utils/get-formatted-additional-fees';
import { getFreeTrialDaysLabel } from '../utils/getFreeTrialDaysLabel';
import { getPeriodLabel } from '../utils/getPeriodLabel';
import { getPlanDuration } from '../utils/getPlanValidityCycle';
import {
  getVariantAdditionalFees,
  getVariantFreeTrialDaysLabel,
  getVariantPeriodLabel,
  getVariantDuration,
} from '../utils/plan-v3-utils';

interface GetTranslatedPricingDetailsContext {
  t: TFunction;
  locale: string | undefined;
  createCurrencyFormatter: CreateCurrencyFormatter;
}

export function createGetTranslatedPricingDetails(
  context: GetTranslatedPricingDetailsContext,
): (plan: PublicPlanV2 | PlanV3) => TranslatedPricingDetails {
  return (plan) =>
    isPlanV3(plan) ? getTranslatedPricingDetailsV3(plan, context) : getTranslatedPricingDetailsV2(plan, context);
}

function getTranslatedPricingDetailsV2(
  plan: PublicPlanV2,
  context: GetTranslatedPricingDetailsContext,
): TranslatedPricingDetails {
  const { value, currency } = getPlanPrice(plan);
  const { t, locale, createCurrencyFormatter } = context;

  const formattedPriceData = getFormattedPriceData({
    createCurrencyFormatter,
    locale,
    value,
    currency,
  });

  return {
    price: formattedPriceData.fullPrice,
    cycle: getPeriodLabel(plan.pricing?.subscription, t) ?? '',
    duration: getPlanDuration(plan, t),
    freeTrial: getFreeTrialDaysLabel(plan, t),
    additionalFees: getFormattedAdditionalFees({
      plan,
      locale,
      createCurrencyFormatter,
    }),
  };
}

function getTranslatedPricingDetailsV3(
  plan: PlanV3,
  context: GetTranslatedPricingDetailsContext,
): TranslatedPricingDetails {
  const variant = plan.pricingVariants![0];
  const { value, currency } = getVariantPrice(variant, plan.currency);
  const { t, locale, createCurrencyFormatter } = context;

  const formattedPriceData = getFormattedPriceData({
    createCurrencyFormatter,
    locale,
    value: value?.toString(),
    currency,
  });

  return {
    price: formattedPriceData.fullPrice,
    cycle: getVariantPeriodLabel(variant, t) ?? '',
    duration: getVariantDuration(variant, t),
    freeTrial: getVariantFreeTrialDaysLabel(variant, t),
    additionalFees: getVariantAdditionalFees({
      variant,
      currency: plan.currency,
      locale,
      createCurrencyFormatter,
    }),
  };
}
